import { useState } from "react";
import { useUpdateContract, getGetContractQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type BuyoutScheduleEntry,
  type TerminationRules,
  type BuyoutFormula,
  DEFAULT_TERMINATION_RULES,
} from "@workspace/buyout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export interface BuyoutTermsValue {
  schedule: BuyoutScheduleEntry[];
  rules: TerminationRules;
  mitigationRequired: boolean;
}

const FORMULA_OPTIONS: { value: BuyoutFormula; label: string }[] = [
  { value: "schedule", label: "Per-year schedule" },
  { value: "remaining_salary", label: "Remaining base salary" },
  { value: "fixed", label: "Flat buyout clause" },
  { value: "none", label: "No payment owed" },
];

export function cleanBuyoutSchedule(schedule: BuyoutScheduleEntry[]): BuyoutScheduleEntry[] {
  return schedule
    .filter((e) => Number.isFinite(e.year) && e.year > 0)
    .map((e) => ({ year: Math.floor(e.year), amount: Math.max(0, Number(e.amount) || 0) }))
    .sort((a, b) => a.year - b.year);
}

interface FieldsProps {
  value: BuyoutTermsValue;
  onChange: (value: BuyoutTermsValue) => void;
}

export function BuyoutTermsFields({ value, onChange }: FieldsProps) {
  const { schedule, rules, mitigationRequired } = value;

  const setSchedule = (next: BuyoutScheduleEntry[]) =>
    onChange({ ...value, schedule: next });
  const setRules = (next: TerminationRules) => onChange({ ...value, rules: next });
  const setMitigationRequired = (next: boolean) =>
    onChange({ ...value, mitigationRequired: next });

  const updateRule = (key: keyof TerminationRules, v: BuyoutFormula) => {
    setRules({ ...rules, [key]: v });
  };

  const updateScheduleEntry = (idx: number, patch: Partial<BuyoutScheduleEntry>) => {
    setSchedule(schedule.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const addScheduleEntry = () => {
    const lastYear = schedule.reduce((m, e) => Math.max(m, e.year), 0);
    setSchedule([...schedule, { year: lastYear + 1, amount: 0 }]);
  };

  const removeScheduleEntry = (idx: number) => {
    setSchedule(schedule.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Per-year buyout schedule</Label>
            <p className="text-xs text-muted-foreground">
              Year is 1-indexed from the contract start date. Amounts typically decline each year.
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addScheduleEntry}>
            <Plus className="h-4 w-4 mr-1" /> Add year
          </Button>
        </div>
        {schedule.length === 0 ? (
          <div className="text-sm text-muted-foreground italic border rounded-md p-3">
            No yearly buyout schedule defined. The calculator will fall back to the flat buyout
            clause or remaining salary depending on the rule applied.
          </div>
        ) : (
          <div className="space-y-2">
            {schedule.map((e, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <Label htmlFor={`year-${i}`} className="text-xs">Contract year</Label>
                  <Input
                    id={`year-${i}`}
                    type="number"
                    min={1}
                    value={e.year}
                    onChange={(ev) => updateScheduleEntry(i, { year: parseInt(ev.target.value) || 1 })}
                  />
                </div>
                <div className="col-span-8">
                  <Label htmlFor={`amount-${i}`} className="text-xs">Buyout amount ($)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-muted-foreground text-sm">$</span>
                    <Input
                      id={`amount-${i}`}
                      type="number"
                      className="pl-7"
                      value={e.amount}
                      onChange={(ev) => updateScheduleEntry(i, { amount: parseFloat(ev.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="col-span-1">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeScheduleEntry(i)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-sm font-medium">Termination-type rules</Label>
          <p className="text-xs text-muted-foreground">
            Pick which formula the calculator applies for each termination scenario.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RuleSelector
            label="Without Cause (by University)"
            value={rules.withoutCauseUniversity}
            onChange={(v) => updateRule("withoutCauseUniversity", v)}
          />
          <RuleSelector
            label="With Cause"
            value={rules.withCause}
            onChange={(v) => updateRule("withCause", v)}
          />
          <RuleSelector
            label="Coach-Initiated Departure"
            value={rules.coachInitiated}
            onChange={(v) => updateRule("coachInitiated", v)}
          />
        </div>
      </div>

      <div className="flex items-center justify-between rounded-md border p-3">
        <div>
          <Label htmlFor="mitigation-required" className="text-sm font-medium">
            Contract requires mitigation
          </Label>
          <p className="text-xs text-muted-foreground">
            When enabled, the calculator defaults the mitigation toggle to on for this contract.
          </p>
        </div>
        <Switch
          id="mitigation-required"
          checked={mitigationRequired}
          onCheckedChange={setMitigationRequired}
        />
      </div>
    </div>
  );
}

interface Props {
  contractId: number;
  initialSchedule: BuyoutScheduleEntry[];
  initialRules: TerminationRules;
  initialMitigationRequired: boolean;
}

export function BuyoutTermsEditor(props: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateContract = useUpdateContract();

  const [value, setValue] = useState<BuyoutTermsValue>({
    schedule: props.initialSchedule?.length ? props.initialSchedule : [],
    rules: props.initialRules ?? DEFAULT_TERMINATION_RULES,
    mitigationRequired: !!props.initialMitigationRequired,
  });

  const handleSave = () => {
    const cleaned = cleanBuyoutSchedule(value.schedule);

    updateContract.mutate(
      {
        id: props.contractId,
        data: {
          buyoutSchedule: cleaned,
          terminationRules: value.rules,
          mitigationRequired: value.mitigationRequired,
        },
      },
      {
        onSuccess: () => {
          toast({ title: "Buyout terms saved", description: "Calculator inputs are now persisted." });
          queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(props.contractId) });
        },
        onError: () => {
          toast({ title: "Error", description: "Could not save buyout terms.", variant: "destructive" });
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-muted-foreground" /> Buyout Terms (calculator inputs)
        </CardTitle>
        <CardDescription>
          Define the per-year buyout schedule, which formula applies to each termination type, and
          whether the contract requires the coach to mitigate damages. These values feed the buyout
          calculator above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <BuyoutTermsFields value={value} onChange={setValue} />
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={updateContract.isPending}>
            {updateContract.isPending ? "Saving..." : "Save buyout terms"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RuleSelector({
  label,
  value,
  onChange,
}: {
  label: string;
  value: BuyoutFormula;
  onChange: (v: BuyoutFormula) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={(v) => onChange(v as BuyoutFormula)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {FORMULA_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
